import { useCallback, useEffect, useMemo } from 'react';
import { shallowEqual, useStore } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { isArtifactDisplayTab, openArtifactDisplay } from '../context/ArtifactDisplayContext';
import { ChatMessage, WebSearchActivity } from '../services/chatService';
import type { RootState } from '../store';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  setMessages, setInputValue, setIsLoading, setError,
  setTypingState, setStreamingDisplay, appendStreamingDisplay, setCurrentStreamingMsgId,
  setProjectTitle, upsertStreamingWebSearch, clearStreamingWebSearches,
} from '../store/slices/planningSlice';

import { RightPanelTab } from './PlanningPageHelpers';
import { usePlanningPageRouting } from './usePlanningPageRouting';

// Layout State Helpers
const useRouterState = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  return { projectId, navigate };
};

export const useChatSelectors = () => {
  return useAppSelector((state) => ({
    messages: state.planning.messages,
    isLoading: state.planning.isLoading,
    error: state.planning.error,
    projectLoadStatus: state.planning.projectLoadStatus,
    projectLoadError: state.planning.projectLoadError,
    hasProjectSnapshot: state.planning.hasProjectSnapshot,
    activeProjectId: state.planning.activeProjectId,
    activeSessionId: state.planning.activeSessionId,
  }), shallowEqual);
};

export const useChatInputValue = () => useAppSelector((state) => state.planning.inputValue);

export const useChatDispatchers = () => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();

  return {
    // Functional updates must read the LIVE store, not a render-synced ref:
    // Redux dispatch is synchronous, so back-to-back updaters (e.g. several
    // SSE chat_message frames in one chunk) compose instead of both reading
    // the same stale snapshot and dropping each other's appends.
    setMessages: useCallback((val: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
      if (typeof val === 'function') dispatch(setMessages(val(store.getState().planning.messages)));
      else dispatch(setMessages(val));
    }, [dispatch, store]),
    setInputValue: useCallback((val: string) => dispatch(setInputValue(val)), [dispatch]),
    setIsLoading: useCallback((val: boolean) => dispatch(setIsLoading(val)), [dispatch]),
    setError: useCallback((val: string | null) => dispatch(setError(val)), [dispatch]),
  };
};

export const useStreamingSelectors = () => {
  const isAiTyping = useAppSelector((state) => state.planning.isAiTyping);
  const streamingDisplay = useAppSelector((state) => state.planning.streamingDisplay);
  const currentStreamingMsgId = useAppSelector((state) => state.planning.currentStreamingMsgId);

  return { isAiTyping, streamingDisplay, currentStreamingMsgId };
};

export const useStreamingDispatchers = (refs: { 
  streamingDisplayRef: { current: string }, 
  isAiTypingRef: { current: boolean } 
}) => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  // We use references to Refs to allow updating them.
  // Although refs are technically mutable, we pass them as objects here.
  const streamingDisplayRef = refs.streamingDisplayRef;

  return {
    setIsAiTyping: useCallback((val: boolean) => dispatch(setTypingState({ isAiTyping: val })), [dispatch]),
    setStreamingDisplay: useCallback((val: string | ((prev: string) => string)) => {
      const nextVal = typeof val === 'function' ? val(streamingDisplayRef.current) : val;

      streamingDisplayRef.current = nextVal;
      dispatch(setStreamingDisplay(nextVal));
    }, [dispatch, streamingDisplayRef]),
    appendStreamingDisplay: useCallback((val: string) => {
      streamingDisplayRef.current += val;
      dispatch(appendStreamingDisplay(val));
    }, [dispatch, streamingDisplayRef]),
    setCurrentStreamingMsgId: useCallback((val: string | null) => dispatch(setCurrentStreamingMsgId(val)), [dispatch]),
    upsertStreamingWebSearch: useCallback(
      (activity: WebSearchActivity) => dispatch(upsertStreamingWebSearch(activity)),
      [dispatch],
    ),
    clearStreamingWebSearches: useCallback(() => dispatch(clearStreamingWebSearches()), [dispatch]),
    /** Read when the reply is saved, so the live store (not a render snapshot) decides what it keeps. */
    getStreamingWebSearches: useCallback(() => store.getState().planning.streamingWebSearches, [store]),
  };
};

const useReduxProjectState = () => {
  const dispatch = useAppDispatch();
  const projectTitle = useAppSelector((state) => state.planning.projectTitle);
  const activeProjectId = useAppSelector((state) => state.planning.activeProjectId);

  return {
    activeProjectId,
    projectTitle,
    setProjectTitle: useCallback((val: string | null) => {
      dispatch(setProjectTitle(val));
    }, [dispatch]),
    project: { name: projectTitle, id: null as string | null }
  };
};

const useReduxPanelState = (
  navigateToTab: (tab: RightPanelTab, itemId?: string | null) => void
) => {
  const activeRightTab = useAppSelector((state) => state.planning.activeRightTab);

  return {
    activeRightTab,
    setActiveRightTab: useCallback((val: RightPanelTab) => navigateToTab(val), [navigateToTab]),
  };
};

const useProjectAdapter = (
  project: ReturnType<typeof useReduxProjectState>,
  projectId: string | undefined
) => useMemo(() => ({
  ...project.project,
  name: project.activeProjectId === projectId ? project.project.name : null,
  id: project.project.id || projectId || null,
}), [project.activeProjectId, project.project, projectId]);

/** Listen for tab-navigation events dispatched by ChatBox components */
const TASKS_VIEWS: ReadonlySet<string> = new Set([
  'list', 'gantt', 'kanban', 'sprint', 'phases', 'epic',
]);

const openPlanningArtifact = (tab: RightPanelTab): boolean => {
  if (!isArtifactDisplayTab(tab)) return false;
  openArtifactDisplay({ tab, source: 'timeline' });

  return true;
};

type WindowEventListener = (event: Event) => unknown;

const subscribeToWindowEvents = (listeners: [string, WindowEventListener][]): (() => void) => {
  listeners.forEach(([name, listener]) => window.addEventListener(name, listener));

  return () => listeners.forEach(([name, listener]) => window.removeEventListener(name, listener));
};

const useTabNavigationListeners = (
  navigateToTab: (tab: RightPanelTab) => void,
  navigateToDesignEditor: (pageName: string) => void,
) => {
  useEffect(() => {
    const tasksHandler = () => openPlanningArtifact('tasks');
    const tasksViewHandler = (e: Event) => {
      const subview = (e as globalThis.CustomEvent<unknown>).detail;

      if (typeof subview !== 'string' || !TASKS_VIEWS.has(subview)) return;
      openArtifactDisplay({ tab: 'tasks', subview, source: 'timeline' });
    };
    const genericHandler = (e: Event) => {
      const tab = (e as globalThis.CustomEvent).detail as RightPanelTab;

      if (!openPlanningArtifact(tab)) navigateToTab(tab);
    };
    const editorHandler = (e: Event) => {
      const detail = (e as globalThis.CustomEvent<{ pageName?: string }>).detail;

      if (detail?.pageName) navigateToDesignEditor(detail.pageName);
    };

    return subscribeToWindowEvents([
      ['switch_to_tasks_tab', tasksHandler],
      ['switch_tasks_view', tasksViewHandler],
      ['navigate_to_tab', genericHandler],
      ['open_design_editor', editorHandler],
    ]);
  }, [navigateToDesignEditor, navigateToTab]);
};

export const usePlanningPageState = () => {
  const router = useRouterState();
  const project = useReduxProjectState();
  const routing = usePlanningPageRouting(router.projectId);
  const panel = useReduxPanelState(routing.navigateToTab);

  useTabNavigationListeners(routing.navigateToTab, routing.navigateToDesignEditor);

  return {
    router,
    project: { ...project, project: useProjectAdapter(project, router.projectId) },
    panel,
    routing,
  };
};
